"use client";

import { useLandingPage } from "@/hooks/pages/useLandingPage";
import { useSitecoreLayout } from "@/hooks/pages/useSitecoreLayout";
import useMonarch from "@/hooks/useMonarch";
import type { LocalizedCustomerState } from "@/types/common";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ExperienceModeProvider } from "./ExperienceModeContext";

type AppContextValue = LocalizedCustomerState & {
  locationDisplay: string;
  isLayoutReady: boolean;
  globalRules: Record<string, unknown>;
  isMonarchLoading: boolean;
};

type StickyBannerContextValue = {
  stickyBannerHeight: number;
  setStickyBannerHeight: (height: number) => void;
  stickyBottomHeight: number;
  setStickyBottomHeight: (height: number) => void;
};

type LayoutReadyContextValue = {
  isLayoutReady: boolean;
};

const AppContext = createContext<AppContextValue | null>(null);
const StickyBannerContext = createContext<StickyBannerContextValue | null>(
  null
);
const LayoutReadyContext = createContext<LayoutReadyContextValue | null>(null);

function hasFixedPositionAncestor(target: HTMLElement) {
  let currentElement: HTMLElement | null = target;

  while (currentElement) {
    if (window.getComputedStyle(currentElement).position === "fixed") {
      return true;
    }

    currentElement = currentElement.parentElement;
  }

  return false;
}

function StickyBannerProvider({ children }: { children: ReactNode }) {
  const [stickyBannerHeight, setStickyBannerHeight] = useState(0);
  const [stickyBottomHeight, setStickyBottomHeight] = useState(0);

  useEffect(() => {
    const focusVisibilityGutter = 8;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      window.requestAnimationFrame(() => {
        if (!document.contains(target)) return;

        if (hasFixedPositionAncestor(target)) return;

        const rect = target.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;

        const rootStyle = window.getComputedStyle(document.documentElement);
        const stickyNavHeight =
          parseFloat(rootStyle.getPropertyValue("--sticky-nav-height")) || 0;
        const topBoundary =
          stickyNavHeight + stickyBannerHeight + focusVisibilityGutter;
        const bottomBoundary =
          window.innerHeight - stickyBottomHeight - focusVisibilityGutter;

        if (rect.top < topBoundary) {
          window.scrollBy({ top: rect.top - topBoundary, behavior: "auto" });
          return;
        }

        if (rect.bottom > bottomBoundary) {
          window.scrollBy({
            top: rect.bottom - bottomBoundary,
            behavior: "auto",
          });
        }
      });
    };

    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [stickyBannerHeight, stickyBottomHeight]);

  const stickyBannerContextValue = useMemo(
    () => ({
      stickyBannerHeight,
      setStickyBannerHeight,
      stickyBottomHeight,
      setStickyBottomHeight,
    }),
    [
      stickyBannerHeight,
      setStickyBannerHeight,
      stickyBottomHeight,
      setStickyBottomHeight,
    ]
  );

  return (
    <StickyBannerContext.Provider value={stickyBannerContextValue}>
      {children}
    </StickyBannerContext.Provider>
  );
}

function LayoutReadyProvider({
  children,
  isLayoutReady,
}: {
  children: ReactNode;
  isLayoutReady: boolean;
}) {
  const layoutReadyContextValue = useMemo(
    () => ({
      isLayoutReady,
    }),
    [isLayoutReady]
  );

  return (
    <LayoutReadyContext.Provider value={layoutReadyContextValue}>
      {children}
    </LayoutReadyContext.Provider>
  );
}

export function AppProvider({ children }: { children: ReactNode }) {
  const landingPageData = useLandingPage();
  const { layout, skipped } = useSitecoreLayout();
  const { globalRules, isMonarchLoading } = useMonarch();

  const isLayoutReady = useMemo(
    () => Boolean(layout) || Boolean(skipped),
    [layout, skipped]
  );

  const contextValue = useMemo(
    () => ({
      ...landingPageData,
      isLayoutReady,
      globalRules,
      isMonarchLoading,
    }),
    [landingPageData, isLayoutReady, globalRules, isMonarchLoading]
  );

  return (
    <AppContext.Provider value={contextValue}>
      <ExperienceModeProvider globalRules={globalRules}>
        <LayoutReadyProvider isLayoutReady={isLayoutReady}>
          <StickyBannerProvider>{children}</StickyBannerProvider>
        </LayoutReadyProvider>
      </ExperienceModeProvider>
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return context;
}

export function useStickyBannerContext() {
  const context = useContext(StickyBannerContext);
  if (!context) {
    throw new Error("useStickyBannerContext must be used within AppProvider");
  }
  return context;
}

export function useLayoutReadyContext() {
  const context = useContext(LayoutReadyContext);
  if (!context) {
    throw new Error("useLayoutReadyContext must be used within AppProvider");
  }
  return context;
}
